import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import personApi, {
    PersonNetworkImportLogStatusValue,
    PersonNetworkImportStatusSummaryDto,
    PersonNetworkImportStatusValue,
} from '../../../api/person/PersonApi'
import networkApi from '../../../api/network/NetworkApi'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import Modal from '../../common/modals/Modal'
import Button from '../../common/form/inputs/Button'
import { useSafeUserData } from '../../../stateStores/User'

const POLL_INTERVAL_MS = 2000

const STATUS_LABELS: Record<PersonNetworkImportStatusValue, string> = {
    EXCLUDED: 'Ready to import',
    IN_PROGRESS: 'Importing...',
    COMPLETE: 'Import complete',
    FAILED: 'Import failed',
    CACHE_INVALID: 'Needs refresh',
}

const LOG_STATUS_LABELS: Record<PersonNetworkImportLogStatusValue, string> = {
    DISPATCH_PENDING: 'Waiting to queue',
    QUEUED: 'Queued',
    STARTED: 'Starting import',
    IMPORT_IN_PROGRESS: 'Importing...',
    IMPORT_COMPLETE: 'Building network',
    NETWORK_GEN_IN_PROGRESS: 'Generating network',
    COMPLETE: 'Complete',
    FAILED: 'Failed',
}

type Props = {
    isOpen: boolean
    personIds: string[]
    personNamesById?: Record<string, string>
    personPhotoUrlsById?: Record<string, string | undefined>
    onComplete: () => void
    onCancel: () => void
}

export type NetworkImportModalState = Pick<
    Props,
    'personIds' | 'personNamesById' | 'personPhotoUrlsById'
>

type ImportPhase = 'idle' | 'checking' | 'queueing' | 'polling' | 'error'

type StatusMap = Record<string, PersonNetworkImportStatusSummaryDto>

function getInitialsFromLabel(label: string): string {
    const cleaned = label.replace(/[•|]/g, ' ')
    const parts = cleaned
        .split(/\s+/)
        .map((part) => part.trim())
        .filter(Boolean)
    const initials = parts
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .filter(Boolean)
        .join('')
    return initials || 'TC'
}

function getStatusLabel(summary?: PersonNetworkImportStatusSummaryDto): string {
    if (!summary) {
        return 'Pending import'
    }
    if (summary.errMsg) {
        return 'Failed'
    }
    if (summary.status === 'IN_PROGRESS' && summary.latestLogStatus) {
        return LOG_STATUS_LABELS[summary.latestLogStatus] ?? 'Importing...'
    }
    return STATUS_LABELS[summary.status] ?? 'Pending import'
}

function normalizeError(err: unknown, fallback: string): string {
    if (err instanceof Error) {
        return err.message.replace(
            /^There was an issue communicating with the Contxt server:\s*/,
            '',
        )
    }
    return fallback
}

export default function PersonNetworkImportModal({
    isOpen,
    personIds,
    personNamesById,
    personPhotoUrlsById,
    onComplete,
    onCancel,
}: Props): React.JSX.Element | null {
    const { user } = useSafeUserData()
    const isProductAdmin = user.isProductAdmin
    const [statusMap, setStatusMap] = useState<StatusMap>({})
    const [targetIds, setTargetIds] = useState<string[]>([])
    const [phase, setPhase] = useState<ImportPhase>('idle')
    const [error, setError] = useState<string | null>(null)
    const [retryToken, setRetryToken] = useState(0)
    const pollTimeoutRef = useRef<number | null>(null)
    const activeRunRef = useRef(0)
    const onCompleteRef = useRef(onComplete)

    useEffect(() => {
        onCompleteRef.current = onComplete
    }, [onComplete])

    useEffect(() => {
        if (!isOpen) {
            setStatusMap({})
            setTargetIds([])
            setPhase('idle')
            setError(null)
            window.clearTimeout(pollTimeoutRef.current ?? undefined)
            pollTimeoutRef.current = null
            return
        }

        const uniquePersonIds = Array.from(new Set(personIds.filter(Boolean)))

        if (uniquePersonIds.length === 0) {
            onCompleteRef.current()
            return
        }

        let isCancelled = false
        activeRunRef.current += 1
        const currentRunId = activeRunRef.current
        window.clearTimeout(pollTimeoutRef.current ?? undefined)
        pollTimeoutRef.current = null

        const finish = () => {
            if (isCancelled || currentRunId !== activeRunRef.current) {
                return
            }
            onCompleteRef.current()
        }

        const fail = (message: string) => {
            if (isCancelled || currentRunId !== activeRunRef.current) {
                return
            }
            setPhase('error')
            setError(message)
            window.clearTimeout(pollTimeoutRef.current ?? undefined)
            pollTimeoutRef.current = null
        }

        const pollStatuses = async (ids: string[]) => {
            try {
                const latest = await personApi.getNetworkImportStatuses(ids)
                if (isCancelled || currentRunId !== activeRunRef.current) {
                    return
                }
                setStatusMap((prev) => ({ ...prev, ...latest }))

                const hasFailure = ids.some((id) => {
                    const status = latest[id]?.status
                    return status === 'FAILED' || status === 'CACHE_INVALID'
                })

                if (hasFailure) {
                    fail(
                        'Network import failed. Please retry before searching.',
                    )
                    return
                }

                const remaining = ids.filter(
                    (id) => latest[id]?.status !== 'COMPLETE',
                )

                if (remaining.length === 0) {
                    finish()
                    return
                }

                pollTimeoutRef.current = window.setTimeout(() => {
                    void pollStatuses(ids)
                }, POLL_INTERVAL_MS)
            } catch (err) {
                fail(normalizeError(err, 'Unable to fetch import status.'))
            }
        }

        const start = async () => {
            setPhase('checking')
            setError(null)
            setTargetIds([])
            setStatusMap({})

            try {
                const statuses =
                    await personApi.getNetworkImportStatuses(uniquePersonIds)
                if (isCancelled || currentRunId !== activeRunRef.current) {
                    return
                }
                setStatusMap(statuses)

                const idsNeedingImport = uniquePersonIds.filter(
                    (id) => statuses[id]?.status !== 'COMPLETE',
                )
                setTargetIds(idsNeedingImport)

                if (idsNeedingImport.length === 0) {
                    finish()
                    return
                }

                const queueIds = idsNeedingImport.filter((id) => {
                    const status = statuses[id]
                    return !status || status.status !== 'IN_PROGRESS'
                })

                if (queueIds.length > 0) {
                    setPhase('queueing')
                    await networkApi.queueNetworkImport(
                        {
                            personIds: queueIds,
                        },
                        isProductAdmin,
                    )
                    if (isCancelled || currentRunId !== activeRunRef.current) {
                        return
                    }
                    setStatusMap((prev) => {
                        const next: StatusMap = { ...prev }
                        for (const id of queueIds) {
                            const existing = prev[id]
                            next[id] = {
                                status: 'IN_PROGRESS',
                                hasStatusRow: true,
                                latestLogStatus:
                                    existing?.latestLogStatus ?? 'QUEUED',
                                errMsg: existing?.errMsg,
                            }
                        }
                        return next
                    })
                }

                setPhase('polling')
                await pollStatuses(idsNeedingImport)
            } catch (err) {
                fail(normalizeError(err, 'Unable to start network import.'))
            }
        }

        void start()

        return () => {
            isCancelled = true
            window.clearTimeout(pollTimeoutRef.current ?? undefined)
            pollTimeoutRef.current = null
        }
    }, [isOpen, personIds, retryToken, isProductAdmin])

    const total = targetIds.length
    const completedCount = useMemo(
        () =>
            targetIds.reduce((count, id) => {
                return statusMap[id]?.status === 'COMPLETE' ? count + 1 : count
            }, 0),
        [targetIds, statusMap],
    )

    const progressPercent = useMemo(() => {
        if (total === 0) {
            return 0
        }
        return Math.round((completedCount / total) * 100)
    }, [completedCount, total])

    const showSpinner = phase !== 'error' && phase !== 'idle'

    const handleRetry = () => {
        setRetryToken((token) => token + 1)
    }

    const handleClose = useCallback(() => {
        if (phase !== 'error') {
            return
        }
        onCancel()
    }, [onCancel, phase])

    const trackedPeople = useMemo(
        () =>
            targetIds.map((id) => ({
                id,
                label: personNamesById?.[id] ?? 'Unknown person',
                status: statusMap[id],
                photoUrl: personPhotoUrlsById?.[id],
            })),
        [targetIds, personNamesById, personPhotoUrlsById, statusMap],
    )

    if (!isOpen) {
        return null
    }

    return (
        <Modal
            title="Preparing your network"
            onClose={handleClose}
            rightFooter={
                phase === 'error' ? (
                    <>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={handleClose}
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            style="primary"
                            onClick={handleRetry}
                        >
                            Retry
                        </Button>
                    </>
                ) : undefined
            }
            allowUserToClose={phase === 'error'}
            size="large"
        >
            <div className="flex flex-col gap-4">
                {phase === 'error' ? (
                    <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">
                        {error ?? 'Unable to import network. Please try again.'}
                    </div>
                ) : (
                    showSpinner && (
                        <div className="flex items-center gap-3 text-sm font-medium text-gray-700">
                            <LoadingSpinner />
                        </div>
                    )
                )}

                {total > 0 && (
                    <div className="space-y-2">
                        <div className="text-right text-xs font-medium text-gray-500">
                            {completedCount} / {total} imported
                        </div>
                        <div className="h-2 rounded-full bg-gray-100">
                            <div
                                className="h-2 rounded-full bg-blue-500 transition-all"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                    </div>
                )}

                {trackedPeople.length > 0 && (
                    <div className="rounded-2xl border border-gray-100">
                        <div className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {trackedPeople.length}{' '}
                            {trackedPeople.length === 1
                                ? 'person needs import'
                                : 'people need import'}
                        </div>
                        <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
                            {trackedPeople.map((person) => (
                                <div
                                    key={person.id}
                                    className="flex items-center justify-between px-4 py-2 text-sm"
                                >
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600">
                                            {person.photoUrl ? (
                                                <img
                                                    src={person.photoUrl}
                                                    alt={person.label}
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <span>
                                                    {getInitialsFromLabel(
                                                        person.label,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate font-medium text-gray-900">
                                                {person.label}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="ml-3 flex-shrink-0 text-xs font-medium text-gray-600">
                                        {getStatusLabel(person.status)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}
